/**
 * TermsModal — the app's terms, read-only. Opened from the More menu.
 *
 * Google's terms require the app to say it uses Google Maps. The text is bundled, so it
 * opens offline. The wording is section 8 of the Run map spec (issue #93), which Garden
 * Bags approved on 5 October 2026 (recorded on #93), so change it only with their
 * approval. Each section is a paragraph in the Stack, so a later section goes below it.
 */

import { Anchor, Stack, Text } from '@mantine/core';
import { ModalFrame } from './ModalFrame';
import type { ReactNode } from 'react';

const GOOGLE_MAPS_TERMS_URL = 'https://maps.google.com/help/terms_maps/';
const GOOGLE_PRIVACY_URL = 'https://policies.google.com/privacy';

interface Props {
  opened: boolean;
  onClose: () => void;
}

export function TermsModal({ opened, onClose }: Props) {
  return (
    <ModalFrame opened={opened} onClose={onClose} title="Terms">
      <Stack gap="lg">
        {/* A link's 44px tap area is taller than a line. At 36px lines it stops short of the
            text on the lines above and below, so a tap on a word never opens the other link. */}
        <Text lh={2.25}>
          This app includes Google Maps features and content. Your use of them is subject to the
          current{' '}
          <ExternalLink href={GOOGLE_MAPS_TERMS_URL}>
            Google Maps/Google Earth Additional Terms of Service
          </ExternalLink>{' '}
          and the <ExternalLink href={GOOGLE_PRIVACY_URL}>Google Privacy Policy</ExternalLink>.
        </Text>
      </Stack>
    </ModalFrame>
  );
}

// ── Small sub-components ──────────────────────────────────────────────────────

/**
 * A link that opens in a new tab. The link stays inline so the sentence wraps as one
 * paragraph. Padding on an inline element grows its tap area without moving the lines,
 * and a line of text is at least 1em high, so this padding reaches the 44px Touch floor.
 */
function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Anchor
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ paddingBlock: 'calc((44px - 1em) / 2)' }}
    >
      {children}
    </Anchor>
  );
}
